import ConditionCheckTypes from './ConditionCheckTypes';
import moment from 'moment';
import { TIME_UNITS } from 'constants/decisionTree';
import { convertToNumber } from 'util/decisionTreeUtil';
import { ConfigurableNodeError } from 'util/error';

export const GENERIC_ERROR_MESSAGE = 'An error occurred while processing the field values in the node';
export const MATHEMATICS_OPERATORS = {
  ADDITION: 'addition',
  SUBTRACTION: 'subtraction',
  MULTIPLICATION: 'multiplication',
  DIVISION: 'division',
};

export const checkCondition = ({ fieldValue, checkType, targetValue, targetUnit }) => {
  switch (checkType) {
    case ConditionCheckTypes.INCLUDES:
      return getFieldIncludesValue(fieldValue, targetValue);
    case ConditionCheckTypes.SOME:
      return getSomeValueExistInField(fieldValue, targetValue);
    case ConditionCheckTypes.EVERY:
      return getEveryValueExistsInField(fieldValue, targetValue);
    case ConditionCheckTypes.EXISTS:
      return !!fieldValue;
    case ConditionCheckTypes.IS_EQUAL:
      return fieldValue === targetValue;
    case ConditionCheckTypes.IS_NOT_EQUAL:
      return fieldValue !== targetValue;
    case ConditionCheckTypes.IS_ONE_OF:
      return getIsValueOneOf(fieldValue, targetValue);
    case ConditionCheckTypes.IS_VALUE_GREATER:
      return getIsValueGreater(fieldValue, targetValue);
    case ConditionCheckTypes.IS_VALUE_GREATER_THAN_OR_EQUAL_TO:
      return getIsValueGreaterThanOrEqualTo(fieldValue, targetValue);
    case ConditionCheckTypes.IS_VALUE_LESS:
      return getIsValueLess(fieldValue, targetValue);
    case ConditionCheckTypes.IS_VALUE_LESS_THAN_OR_EQUAL_TO:
      return getIsValueLessThanOrEqualTo(fieldValue, targetValue);
    case ConditionCheckTypes.IS_TIME_AFTER:
      return getIsTimeAfter(fieldValue, targetValue, targetUnit);
    case ConditionCheckTypes.IS_TIME_AFTER_OR_ON:
      return getIsTimeAfterOrOn(fieldValue, targetValue, targetUnit);
    case ConditionCheckTypes.IS_TIME_BEFORE:
      return !getIsTimeAfterOrOn(fieldValue, targetValue, targetUnit);
    case ConditionCheckTypes.IS_TIME_BEFORE_OR_ON:
      return !getIsTimeAfter(fieldValue, targetValue, targetUnit);
    default:
      throw new ConfigurableNodeError(`An invalid condition check was used (${checkType})`);
  }
};

// Validators
const convertToArray = (value) => {
  if (!value) {
    throw new ConfigurableNodeError(GENERIC_ERROR_MESSAGE);
  }

  return Array.isArray(value) ? value : [value];
};

const convertToDateTime = (value) => {
  if (!value) {
    throw new ConfigurableNodeError(GENERIC_ERROR_MESSAGE);
  }

  const dateTime = new Date(value).getTime();
  if (Number.isNaN(dateTime)) {
    throw new ConfigurableNodeError(GENERIC_ERROR_MESSAGE);
  }

  return dateTime;
};

// Operators
const getFieldIncludesValue = (fieldValue, targetValue) => {
  if (!Array.isArray(fieldValue)) {
    throw new ConfigurableNodeError(GENERIC_ERROR_MESSAGE);
  }

  return fieldValue.includes(targetValue);
};

const getSomeValueExistInField = (fieldValue, targetValue) => {
  const fieldArray = convertToArray(fieldValue);
  const valueArray = convertToArray(targetValue);

  return fieldArray.some((fieldItem) => valueArray.includes(fieldItem));
};

const getEveryValueExistsInField = (fieldValue, targetValue) => {
  const fieldArray = convertToArray(fieldValue);
  const valueArray = convertToArray(targetValue);

  return fieldArray.every((fieldItem) => valueArray.includes(fieldItem));
};

const getIsValueOneOf = (fieldValue, targetValue) => {
  if (!Array.isArray(targetValue)) {
    throw new ConfigurableNodeError(GENERIC_ERROR_MESSAGE);
  }

  return targetValue.includes(fieldValue);
};

const getIsValueGreater = (fieldValue, targetValue) => {
  const numericFieldValue = convertToNumber(fieldValue);
  const numericTargetValue = convertToNumber(targetValue);

  return numericFieldValue > numericTargetValue;
};

const getIsValueGreaterThanOrEqualTo = (fieldValue, targetValue) => {
  const numericFieldValue = convertToNumber(fieldValue);
  const numericTargetValue = convertToNumber(targetValue);

  return numericFieldValue >= numericTargetValue;
};

const getIsValueLess = (fieldValue, targetValue) => {
  const numericFieldValue = convertToNumber(fieldValue);
  const numericTargetValue = convertToNumber(targetValue);

  return numericFieldValue < numericTargetValue;
};

const getIsValueLessThanOrEqualTo = (fieldValue, targetValue) => {
  const numericFieldValue = convertToNumber(fieldValue);
  const numericTargetValue = convertToNumber(targetValue);

  return numericFieldValue <= numericTargetValue;
};

const getIsTimeAfter = (fieldValue, targetValue, targetUnit) => {
  if (!targetUnit || !TIME_UNITS.includes(targetUnit.toUpperCase())) {
    throw new ConfigurableNodeError(GENERIC_ERROR_MESSAGE);
  }
  const targetValueFormatted = moment().subtract(convertToNumber(targetValue), targetUnit);
  const fieldValueTime = convertToDateTime(fieldValue);
  const targetValueTime = convertToDateTime(targetValueFormatted);

  return fieldValueTime > targetValueTime;
};

const getIsTimeAfterOrOn = (fieldValue, targetValue, targetUnit) => {
  if (!targetUnit || !TIME_UNITS.includes(targetUnit.toUpperCase())) {
    throw new ConfigurableNodeError(GENERIC_ERROR_MESSAGE);
  }
  const targetValueFormatted = moment().subtract(convertToNumber(targetValue), targetUnit);
  const fieldValueTime = convertToDateTime(fieldValue);
  const targetValueTime = convertToDateTime(targetValueFormatted);

  return fieldValueTime >= targetValueTime;
};

export const calculateResult = (fieldValue1, operator, fieldValue2) => {
  const fieldValue1AsNumber = convertToNumber(fieldValue1);
  const fieldValue2AsNumber = convertToNumber(fieldValue2);
  if (!operator || !fieldValue2AsNumber) {
    return fieldValue1AsNumber;
  }
  switch (operator) {
    case MATHEMATICS_OPERATORS.ADDITION:
      return fieldValue1AsNumber + fieldValue2AsNumber;
    case MATHEMATICS_OPERATORS.SUBTRACTION:
      return fieldValue1AsNumber - fieldValue2AsNumber;
    case MATHEMATICS_OPERATORS.MULTIPLICATION:
      return fieldValue1AsNumber * fieldValue2AsNumber;
    case MATHEMATICS_OPERATORS.DIVISION:
      return fieldValue1AsNumber / fieldValue2AsNumber;
    default:
      throw new ConfigurableNodeError(`An invalid mathematics operator was used (${operator})`);
  }
};
